// ─────────────────────────────────────────────────────────────────────────────
// United States localisation
//
// Mirrors shared/ca-localise.ts. UK is the source of truth; this file
// transforms UK-flavoured strings into US construction English (closest
// cousin of Canadian English with US spellings — "vapor" not "vapour",
// "color" not "colour", "fiber" not "fibre", "aluminum" not "aluminium",
// AHJ in place of Building Department, IRC/IBC/NEC in place of NBC).
//
// US is the only market that uses IMPERIAL units. The exported helper
// `convertToImperial` translates metric quantities + unit-costs to
// imperial (sq ft / lf / cy / each) while preserving the line subtotal
// (qty × unitCost stable to 2 dp). `localiseUsLineItems` chains
// terminology → category fix → imperial conversion → rate cap.
//
// Every public function is a no-op when market !== "us" so UK, AU and CA
// pipelines are byte-identical.
// ─────────────────────────────────────────────────────────────────────────────

// Currency / cost scaling factor used when converting GBP-denominated template
// rates and AI prompt caps into local USD values.
//
// Calibration: US residential build costs sit roughly 1.5–1.8× UK delivered
// cost on a per-imperial-unit basis after currency conversion. We use 1.7 as
// a defensible mid-point; tune against real won quotes. Note this multiplier
// applies BEFORE the metric→imperial unit conversion, so a UK template item
// at £45/m² becomes ~$76/m² post-factor, which converts to ~$7.10/sq ft —
// at the high end of US drywall+finish but plausible for premium markets.
export const US_PRICE_FACTOR = 1.7;

// Imperial conversion factors (to 5 dp; preserves subtotal to 2 dp).
const M2_TO_SQFT = 10.7639;       // 1 m² = 10.7639 sq ft
const M_TO_FT    = 3.2808;        // 1 m   = 3.2808 ft
const M3_TO_CY   = 1.30795;       // 1 m³ = 1.30795 cubic yards

// UK term → US term replacements. Order matters: more specific first.
//
// Note on case preservation: the rules below mostly use the case-insensitive
// `gi` flag with lowercase replacements (good for mid-sentence body text).
// Template item NAMES are Title Case ("Skip Hire", "Plasterboard"), so we
// register explicit Title Case overrides FIRST. They match before the
// generic lowercase rules and produce "Dumpster Rental", "Drywall", etc.
export const US_TERMINOLOGY: Array<[RegExp, string | ((m: string) => string)]> = [
  // ── Compound phrases (HIGHEST priority — must run before any token-level
  //    rule that would partially consume them) ──
  [/\bBuilding Regulations Part L\b/gi, "IECC (energy code)"],
  [/\bBuilding Regulations Part A\b/gi, "IBC / IRC structural"],
  [/\bFirst and Second Fix\b/g, "Rough-In and Trim-Out"],
  [/\bfirst and second fix\b/gi, "rough-in and trim-out"],
  // Dot-and-dab (gluing drywall to a solid wall) is rare in US wood-frame
  // residential construction — most US drywall goes onto stud framing with
  // screws. The closest US name is just "Drywall Installation"; the AI's
  // output should drop the technique-specific qualifier entirely.
  [/\bDot & Dab Plasterboard\b/g, "Direct-Applied Drywall"],
  [/\bDot and Dab Plasterboard\b/g, "Direct-Applied Drywall"],
  [/\bAdhesive Bonding Drywall\b/g, "Drywall Installation"],
  [/\bCeiling Plasterboard\b/g, "Ceiling Drywall"],
  [/\bPlaster Skim Coat\b/g, "Drywall Finishing"],
  [/\bT&G Chipboard Flooring\b/g, "T&G Subfloor"],
  [/\bLoft Conversions?\b/g, "Attic Conversion"],
  [/\bon dabs\b/gi, "with adhesive"],
  // UK "inner/outer leaf|skin|wythe|wall" — pair-form FIRST so the bare-form
  // rules below don't half-match.
  [/\binner and (?:outer|exterior) (?:leaf|skin|wythe|wall)\b/gi, "interior and exterior wythe"],
  [/\b(?:outer|exterior) and inner (?:leaf|skin|wythe|wall)\b/gi, "interior and exterior wythe"],
  [/\binner (?:leaf|skin|wythe|wall)\b/gi, "interior wythe"],
  [/\bouter (?:leaf|skin|wythe|wall)\b/gi, "exterior wythe"],
  [/\bcentral heating circuit\b/gi, "heating supply"],
  // UK cavity-wall concepts that don't exist in US wood-frame residential
  // construction. Translate to the closest US equivalent so any AI-generated
  // line at least makes sense if it slips through despite the prompt warnings.
  [/\bDamp Proof Course\b/g, "Moisture Barrier"],
  [/\bdamp proof course\b/gi, "moisture barrier"],
  [/\bDPC\b/g, "moisture barrier"],
  [/\bDPM\b/g, "vapor barrier"],
  [/\bCavity Closers?\b/g, "Air-Sealing at Openings"],
  [/\bcavity closers?\b/gi, "air-sealing at openings"],
  [/\bCavity Trays?\b/g, "Through-Wall Flashing"],
  [/\bcavity trays?\b/gi, "through-wall flashing"],
  [/\bcavity walls?\b/gi, "exterior walls"],
  [/\bCavity Walls?\b/g, "Exterior Walls"],

  // ── Title Case single-token overrides (for template item names) ──
  [/\bSkip Hire\b/g, "Dumpster Rental"],
  [/\bSkip Bins?\b/g, "Dumpsters"],
  [/\bDot & Dab\b/g, "Adhesive"],
  [/\bPlasterboard\b/g, "Drywall"],
  [/\bSkirting Boards?\b/g, "Baseboards"],
  [/\bSkirting\b/g, "Baseboard"],
  [/\bArchitraves?\b/g, "Casing"],
  [/\bIronmongery\b/g, "Hardware"],
  // "Strip-out" / "soft strip" are the UK words for selective demolition;
  // US trade language is "tear-out" (or "demo"). Both Title Case (line-item
  // headers) and lowercase (mid-sentence prose) variants need to flip.
  [/\bSoft Strip(?:-?out)?\b/g, "Selective Tear-Out"],
  [/\bStrip-?Out\b/g, "Tear-Out"],
  [/\bsoft strip(?:-?out)?\b/gi, "selective tear-out"],
  [/\bstrip-?out\b/gi, "tear-out"],
  // "Plastering" / "Skim Coat" appear inside cross-reference bullets like
  // "see Plastering" or "Skim coat (see Plastering)" — translate the Title
  // Case variants too so the inclusions/exclusions list reads natively.
  [/\bPlastering\b/g, "Drywall Finishing"],
  [/\bSkim Coat\b/g, "Drywall Mud / Skim Coat"],
  // "Tanking" is UK plumber/builder slang for waterproofing wet rooms.
  // Strip it from compound names ("Waterproofing / Tanking" → "Waterproofing")
  // and translate solo references too.
  [/\bWaterproofing\s*\/\s*Tanking\b/gi, "Waterproofing"],
  [/\bTanking\b/g, "Waterproofing"],
  [/\btanking\b/g, "waterproofing"],
  // Bathroom-quote leaks the user flagged on Q-2026-0148: "shaver outlet"
  // (UK term for a small, isolated 110 V socket beside the basin) and
  // "towel radiator" / "heated towel rail" (UK central-heating accessory).
  // US equivalents: GFCI outlet, heated towel rack.
  //
  // Pitfall: AI sometimes emits "heated towel radiator" — a naive `towel radiators?`
  // → `heated towel rack` rule then produces "heated heated towel rack" (Q-2026-8314).
  // Fix: optional non-capturing `(?:heated\s+)?` prefix consumes any leading "heated "
  // BEFORE the substitution, so both "towel radiator" and "heated towel radiator"
  // collapse to a single "heated towel rack". Same approach for "heated towel rail".
  [/\bShaver Outlets?\b/g, "GFCI Outlet"],
  [/\bShaver Sockets?\b/g, "GFCI Outlet"],
  [/\bshaver outlets?\b/gi, "GFCI outlet"],
  [/\bshaver sockets?\b/gi, "GFCI outlet"],
  [/\bTowel Radiators?\b/g, "Heated Towel Rack"],
  [/\bHeated Towel Rails?\b/g, "Heated Towel Rack"],
  [/\b(?:heated\s+)?towel radiators?\b/gi, "heated towel rack"],
  [/\b(?:heated\s+)?towel rails?\b/gi, "heated towel rack"],
  // Kitchen-side leaks (architect-suggested follow-up): "worktop" → "countertop",
  // "kitchen units / cupboards" → "cabinets". Constrained to kitchen vocabulary
  // so we don't flip generic "cupboard" elsewhere or cabinet hardware bullets.
  [/\bWorktops?\b/g, "Countertop"],
  [/\bworktops?\b/gi, "countertop"],
  [/\bKitchen Units?\b/g, "Kitchen Cabinets"],
  [/\bkitchen units?\b/gi, "kitchen cabinets"],
  [/\bKitchen Cupboards?\b/g, "Kitchen Cabinets"],
  [/\bkitchen cupboards?\b/gi, "kitchen cabinets"],
  // Door "lining" is the UK builder name for the casing-bearing frame inside
  // an opening; in US carpentry this is the door "jamb" (or "frame" when
  // packaged). Only translate when the context is clearly door-related so
  // generic uses of "lining" (e.g. "wall lining", "pipe lining") stay safe.
  [/\bdoor linings?\b/gi, "door jamb"],
  [/\blining\(s\)\s+and\s+(hardware|ironmongery)\b/gi, "jamb(s) and $1"],
  [/\blinings?\s+and\s+(hardware|ironmongery)\b/gi, "jambs and $1"],
  // "Supply and fit" is the UK trade convention; US trades say "supply and
  // install" (or "furnish and install" in commercial spec language).
  [/\bSupply and Fit\b/g, "Supply and Install"],
  [/\bsupply and fit\b/gi, "supply and install"],

  // ── Bathroom-quote leaks (Q-2026-6266 follow-up) ──
  // "Sanitaryware" is the UK trade umbrella for toilet+basin+bath+vanity;
  // US construction calls these "plumbing fixtures". The (Labour Only)
  // suffix is also a UK convention — US uses (Labor Only).
  [/\bSanitaryware Installation\b/g, "Plumbing Fixtures Installation"],
  [/\bSanitaryware\b/g, "Plumbing Fixtures"],
  [/\bsanitaryware\b/gi, "plumbing fixtures"],
  // (Labour Only) → (Labor Only). Cover both the parenthesised form (item
  // name suffix) and the bare phrase (description body).
  [/\(Labour Only\)/g, "(Labor Only)"],
  [/\(labour only\)/gi, "(labor only)"],
  [/\bLabour Only\b/g, "Labor Only"],
  [/\blabour only\b/gi, "labor only"],
  [/\bLabour\b/g, "Labor"],
  [/\blabour\b/gi, "labor"],
  // Bathroom fittings — UK shower screen → US shower door, UK shower tray
  // → US shower base/pan, UK extractor fan → US exhaust/bath fan.
  [/\bShower Screens?\s*\/\s*Enclosures?\b/g, "Shower Doors / Enclosures"],
  [/\bshower screens?\s*\/\s*enclosures?\b/gi, "shower doors / enclosures"],
  [/\bShower Screens?\b/g, "Shower Door"],
  [/\bshower screens?\b/gi, "shower door"],
  [/\bShower Trays?\b/g, "Shower Base"],
  [/\bshower trays?\b/gi, "shower base"],
  [/\bExtractor Fans?\b/g, "Exhaust Fan"],
  [/\bextractor fans?\b/gi, "exhaust fan"],
  // "basin" → "sink", "bath" (standalone, NOT "bathroom"/"bathtub" which
  // \b...\b protects against). Restrict to bathroom contexts via word
  // boundaries; generic kitchen "sink" stays unaffected.
  [/\bWash Basins?\b/g, "Sink"],
  [/\bwash basins?\b/gi, "sink"],
  [/\bBasins?\b/g, "Sink"],
  [/\bbasins?\b/gi, "sink"],
  // "Bath" → "Tub" — but skip US room-name shorthand ("Half Bath", "Primary
  // Bath", "Master Bath", "Powder Bath", "En-suite Bath", "Full Bath",
  // "Three-Quarter Bath", "Jack and Jill Bath", "Hall/Guest/Family/Kids' Bath")
  // where "Bath" means the room, not the fixture.
  [/(?<!\b(?:Half|Full|Primary|Master|Main|Powder|Hall|Guest|Family|Kids?'?|Children'?s|En[\- ]?suite|Three[\- ]?Quarter|Jack[\- ]and[\- ]Jill)\s)Baths?\b/g, "Tub"],
  [/(?<!\b(?:half|full|primary|master|main|powder|hall|guest|family|kids?'?|children'?s|en[\- ]?suite|three[\- ]?quarter|jack[\- ]and[\- ]jill)\s)baths?\b/gi, "tub"],
  // "X No." is a UK quantity-shorthand ("4 No. doors" = "4 count of doors").
  // Drop the "No." after a number — US trade just writes "4 doors".
  [/(\d+)\s+No\.\s+/g, "$1 "],
  // "(s)" is a UK convention for grammatical number-agnostic words used in
  // template descriptions (e.g. "internal door(s), jamb(s)"). For common
  // bathroom/door nouns we'd rather just pluralise.
  [/\binternal door\(s\)/gi, "internal doors"],
  [/\bdoor\(s\)/gi, "doors"],
  [/\bjamb\(s\)/gi, "jambs"],
  [/\bopening\(s\)/gi, "openings"],
  [/\bwindow\(s\)/gi, "windows"],
  // UK construction terms with no US equivalent left untranslated:
  //   "boxing" / "boxing-in"  → US uses "framing-out" (or "soffits and chases")
  //   "joinery"               → US uses "millwork"
  //   "sanitary fixtures"     → US says "plumbing fixtures" (matches US category label)
  //   "reinstate(d|ment)"     → US uses "restore(d|ation)"
  // These leaked through Q-2026-3812 in the Carpentry, Decorating, Plumbing
  // Fixtures Installation, and Terms & Conditions copy.
  [/\bboxing[\- ]in\b/gi, "framing-out"],
  [/\bBoxing\b/g, "Framing-Out"],
  [/\bboxing\b/g, "framing-out"],
  [/\bJoinery\b/g, "Millwork"],
  [/\bjoinery\b/g, "millwork"],
  [/\bSanitary Fixtures?\b/g, "Plumbing Fixtures"],
  [/\bsanitary fixtures?\b/gi, "plumbing fixtures"],
  [/\bReinstatement\b/g, "Restoration"],
  [/\breinstatement\b/gi, "restoration"],
  [/\bReinstated\b/g, "Restored"],
  [/\breinstated\b/gi, "restored"],
  [/\bReinstating\b/g, "Restoring"],
  [/\breinstating\b/gi, "restoring"],
  [/\bReinstate\b/g, "Restore"],
  [/\breinstate\b/gi, "restore"],
  // "Preliminaries" is the UK/RICS NRM cost-plan section heading; the US
  // construction-industry equivalent is "General Conditions" (CSI MasterFormat
  // Division 01). Translate the standalone heading and the common compound.
  [/\bPreliminaries\s*&\s*Site Setup\b/g, "General Conditions & Site Setup"],
  [/\bPreliminaries\s+and\s+Site Setup\b/g, "General Conditions and Site Setup"],
  [/\bPreliminaries\b/g, "General Conditions"],
  [/\bpreliminaries\b/gi, "general conditions"],
  [/\bPadstones?\b/g, "Bearing Plates"],
  [/\bAcrow Props?\b/g, "Shore Posts"],
  [/\bRainwater Goods\b/g, "Gutters and Downspouts"],
  [/\bHeating Extension\b/g, "Heating Connection"],
  [/\bWindow Boards\b/g, "Window Sills"],
  [/\bT&G Chipboard\b/g, "T&G Subfloor"],
  [/\bChipboard\b/g, "Particleboard"],
  [/\bLofts?\b/g, "Attic"],
  [/\bCloakroom\b/g, "Powder Room"],
  [/\bConsumer Units?\b/g, "Electrical Panel"],
  [/\bFirst Fix\b/g, "Rough-In"],
  [/\bSecond Fix\b/g, "Trim-Out"],
  [/\bSnagging\b/g, "Punch List"],
  [/\bTimber\b/g, "Lumber"],
  [/\bEavestroughs?\b/g, "Gutters"],

  // ── Generic lowercase rules (mid-sentence body text) ──
  // Materials & finishes
  [/\bplasterboard\b/gi, "drywall"],
  [/\bplastering\b/gi, "drywall finishing"],
  [/\bskim coat\b/gi, "drywall mud / skim coat"],
  [/\bskimming\b/gi, "drywall finishing"],
  [/\bdot and dab\b/gi, "adhesive"],
  [/\bdot & dab\b/gi, "adhesive"],
  // Some AI outputs already use "adhesive bonding" (a UK industry phrasing);
  // collapse the redundant "adhesive bonding adhesive" to plain "adhesive"
  // and keep solo "adhesive bonding" → "construction adhesive" for fluency.
  [/\badhesive bonding adhesive\b/gi, "adhesive"],
  [/\bAdhesive Bonding\b/g, "Construction Adhesive"],
  [/\badhesive bonding\b/gi, "construction adhesive"],
  [/\brendering\b/gi, "stucco"],
  [/\brender\b/gi, "stucco"],
  [/\bskirting boards?\b/gi, "baseboards"],
  [/\bskirting\b/gi, "baseboard"],
  [/\barchitraves?\b/gi, "casing"],
  [/\bironmongery\b/gi, "hardware"],
  [/\btimber\b/gi, "lumber"],
  [/\beavestroughs?\b/gi, "gutters"],

  // Structural
  [/\bRSJ\b/g, "steel beam (W-section)"],
  [/\bpadstones?\b/gi, "bearing plates"],
  [/\bAcrow props?\b/gi, "shore posts"],
  [/\bstrongboys?\b/gi, "needle beam supports"],
  [/\bstud work\b/gi, "framing"],
  [/\bstudwork\b/gi, "framing"],

  // Approvals & code
  [/\bBuilding Control\b/g, "AHJ (Authority Having Jurisdiction)"],
  [/\bBuilding Department\b/g, "AHJ (Authority Having Jurisdiction)"],
  [/\bBuilding Regulations\b/g, "IRC / IBC"],
  [/\bPlanning Permission\b/gi, "Building Permit"],
  [/\bPlanning Application\b/gi, "Building Permit Application"],
  [/\bPart A\b/g, "IBC / IRC structural"],
  [/\bPart L\b/g, "IECC (energy code)"],
  // Canadian National Building Code reference — if a CA-flavoured string slips
  // through to a US user (template, share-link from a CA builder, etc.), point
  // them at the residential-default US equivalent. CSA → ICC for the same
  // reason (standards body for code language).
  [/\bNBC\b/g, "IRC"],
  [/\bNational Building Code\b/g, "International Residential Code (IRC)"],
  [/\bCSA\b/g, "ICC"],

  // Drainage / plumbing
  [/\bfoul drains?\b/gi, "sanitary drains"],
  [/\bsoil pipes?\b/gi, "soil stacks"],
  [/\bUPVC\b/g, "PVC"],
  [/\btaps?\b/gi, "faucet"],
  [/\bWC\b/g, "toilet"],

  // Roof
  [/\bVelux\b/g, "skylight"],
  [/\blofts?\b/gi, "attic"],
  [/\bloft conversions?\b/gi, "attic conversion"],

  // Trades & sequencing
  [/\bfirst fix\b/gi, "rough-in"],
  [/\bsecond fix\b/gi, "trim-out / finish"],

  // Electrical hardware
  [/\bconsumer units?\b/gi, "electrical panel"],
  [/\bRCDs?\b/g, "GFCI breaker"],
  [/\bRCBOs?\b/g, "AFCI/GFCI breaker"],
  [/\bMCBs?\b/g, "breaker"],

  // Concrete / fill
  [/\bscreed\b/gi, "concrete topping"],
  [/\bhardcore\b/gi, "compacted granular fill"],
  [/\bground floor slab\b/gi, "slab on grade"],

  // Insulation
  [/\binsulation quilt\b/gi, "batt insulation"],
  [/\bKnauf\b/g, "Owens Corning / CertainTeed"],

  // Snags / payments
  [/\bsnagging\b/gi, "punch list"],
  [/\bprovisional sums?\b/gi, "allowances"],

  // Site / civil
  [/\bblock paving\b/gi, "interlocking pavers"],
  [/\bblock work\b/gi, "concrete block (CMU)"],
  [/\bblockwork\b/gi, "concrete block (CMU)"],
  [/\btarmac\b/gi, "asphalt"],
  [/\bkerbs?\b/gi, "curb"],

  // Waste / equipment
  [/\bskip bins?\b/gi, "dumpster"],
  [/\bskip hire\b/gi, "dumpster rental"],
  [/\bskips?\b/gi, "dumpster"],

  // Misc
  [/\blifts?\b/gi, "elevator"],
  [/\bverandahs?\b/gi, "porch"],

  // ── US spelling overrides (run LAST so they catch words introduced by
  //    earlier replacements as well as any UK spellings remaining in the
  //    source). Single substitution patterns; the lowercase replacement is
  //    fine — these almost always appear inside body text. ──
  [/\bvapour\b/gi, "vapor"],
  [/\bvapours\b/gi, "vapors"],
  [/\bcolours\b/gi, "colors"],
  [/\bcolour\b/gi, "color"],
  [/\bcoloured\b/gi, "colored"],
  [/\bcolouring\b/gi, "coloring"],
  [/\bfibre\b/gi, "fiber"],
  [/\baluminium\b/gi, "aluminum"],
  [/\bcentimetres?\b/gi, "centimeters"],
  [/\bmetres?\b/gi, "meters"],
  [/\bcatalogues?\b/gi, "catalogs"],
  [/\bpractised\b/gi, "practiced"],
  [/\borganis(e|ed|ing|ation)\b/gi, "organiz$1"],
  [/\brealis(e|ed|ing)\b/gi, "realiz$1"],
];

// ─────────────────────────────────────────────────────────────────────────────
// Per-category US rate sanity caps (USD, supply + install, ex-Sales Tax).
// Caps are in IMPERIAL units to match the post-conversion line items.
// Catches AI overshoots and template multipliers that go too high; floors
// catch obvious AI underpricing.
// Calibrate against real won US quotes — keep slightly above realistic top-end.
// ─────────────────────────────────────────────────────────────────────────────
export interface RateCap {
  units: string[];
  maxUnitCost: number;
  /** Optional floor — clamps obvious AI underpricing UP to a realistic US rate. */
  minUnitCost?: number;
}

// Note on unit aliases: convertToImperial emits "sq ft" (with the space) for
// area, "lf" for length, "cy" for volume. Cap unit lists MUST include those
// exact emitted strings or capping post-conversion silently no-ops. We also
// keep the no-space "sqft" + "sf" + "ft²"/"ft2" forms so caps fire when an AI
// or template author hands us already-imperial items in any common shape.
export const US_RATE_CAPS: Record<string, RateCap> = {
  // Per square foot
  drywall:        { units: ["sq ft", "sqft", "sf", "ft²", "ft2", "square feet"], maxUnitCost: 3.50, minUnitCost: 1.50 },  // 1/2" board supply+install
  drywall_finish: { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 4.50, minUnitCost: 2.00 },                 // tape & mud Level 4
  decorating:     { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 4.00, minUnitCost: 1.80 },                 // 2-coat paint
  insulation:     { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 5.50, minUnitCost: 2.00 },                 // R-value batts supply+install
  flooring:       { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 17.00 },                                   // laminate to engineered hardwood
  wall_tiling:    { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 13.00 },                                   // wall ceramic/porcelain supply+set
  floor_tiling:   { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 15.00 },                                   // floor ceramic/porcelain supply+set
  tiling:         { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 15.00 },                                   // generic tile fallback
  waterproofing:  { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 11.00 },                                   // pan liner / membrane to wet area
  brickwork:      { units: ["sq ft", "sqft", "sf", "ft²", "ft2"], maxUnitCost: 30.00, minUnitCost: 22.00 },               // brick veneer supply+lay
  // Per linear foot
  carpentry_trim: { units: ["lf", "lin ft", "lin_ft", "lin. ft", "linear ft", "linear feet", "l/f", "ft"], maxUnitCost: 9.00, minUnitCost: 3.00 },        // baseboard / casing per lf
  strip_footing:  { units: ["lf", "lin ft", "linear feet", "ft"], maxUnitCost: 35.00, minUnitCost: 18.00 },      // poured concrete in trench
  // Each / item
  windows:        { units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 2200, minUnitCost: 700 },     // vinyl window supply+install
  bearing_plate:  { units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 450 },                        // catch beam-priced bearing plate overshoots
  sanitaryware_install:    { units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 2000 },              // labour-only full suite
  bath_accessories_install:{ units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 950 },               // suite of 4-5 accessories
  shower_screen_install:   { units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 700 },               // labour-only
  towel_rail_install:      { units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 675 },               // labour-only
  dumpster:                { units: ["each", "ea", "no", "no.", "nr", "item"], maxUnitCost: 800 },               // single dumpster on small remodel
};

function resolveCapKey(category?: string, name?: string): keyof typeof US_RATE_CAPS | null {
  const c = (category || "").toLowerCase();
  const n = (name || "").toLowerCase();

  // Order matters — most specific patterns first so they're not pre-empted
  // by broader category matches.
  if (n.includes("bearing plate") || n.includes("padstone")) return "bearing_plate";
  if (n.includes("strip footing") || (n.includes("footing") && (c.includes("foundation") || c.includes("concrete")))) return "strip_footing";
  // Only floor "window" lines that look like SUPPLY+INSTALL of new windows.
  // Skip lines that are repair/removal/protection/board/sill/reveal/film/etc.
  if (
    n.includes("window") &&
    !n.includes("window board") &&
    !n.includes("window sill") &&
    !n.includes("window reveal") &&
    !n.includes("window film") &&
    !n.includes("window cleaning") &&
    !n.includes("window protection") &&
    !n.includes("window removal") &&
    !n.includes("remove window") &&
    !n.includes("repair") &&
    !n.includes("restoration") &&
    !n.includes("trickle vent")
  ) return "windows";
  if (n.includes("drywall finish") || n.includes("tape and mud") || n.includes("tape & mud") || n.includes("skim coat") || n.includes("mud and tape")) return "drywall_finish";
  if (c.includes("plaster") || c.includes("drywall") || n.includes("drywall") || n.includes("plasterboard")) return "drywall";
  if (c.includes("decorat") || c.includes("paint") || n.includes("painting")) return "decorating";
  if (c.includes("insulat") || n.includes("insulation") || n.includes("batt")) return "insulation";
  // Bathroom labour-only line items (must precede generic floor/plumbing matches)
  if (n.includes("sanitaryware install") || n.includes("plumbing fixtures install") || n.includes("plumbing fixture install")) return "sanitaryware_install";
  if (n.includes("bathroom accessor") || (n.includes("accessories") && n.includes("install") && (n.includes("labor") || n.includes("labour")))) return "bath_accessories_install";
  if (n.includes("shower screen") || n.includes("shower enclosure") || n.includes("shower door")) return "shower_screen_install";
  // Towel-rail cap intentionally requires explicit "towel" context — a generic
  // "Radiator Installation" in a non-bathroom (heating) job has a higher
  // legitimate ceiling and shouldn't be capped at the bathroom labour rate.
  if (n.includes("towel rail") || n.includes("towel warmer") || (n.includes("radiator") && n.includes("install") && (n.includes("towel") || c.includes("bathroom")))) return "towel_rail_install";
  if (n.includes("waterproof") || n.includes("tanking") || n.includes("pan liner")) return "waterproofing";
  if (n.includes("dumpster") || n.includes("disposal bin") || n.includes("waste bin")) return "dumpster";
  // Tile splits — wall vs floor — must be checked BEFORE the generic "flooring"
  // category check below, otherwise an AI emit of category "floor_tiling" /
  // "floor tiling" returns the broader flooring cap instead of the tile cap.
  if (c.includes("til") && (n.includes("wall til") || n.includes("backsplash"))) return "wall_tiling";
  if ((c.includes("til") || c.includes("floor")) && n.includes("floor til")) return "floor_tiling";
  if (c.includes("til")) return "tiling";
  if (c.includes("floor") && !n.includes("subfloor")) return "flooring";
  if (c.includes("brick") || c.includes("blockwork") || c.includes("masonry")) return "brickwork";
  if (n.includes("baseboard") || n.includes("casing") || n.includes("skirting") || n.includes("architrave")) return "carpentry_trim";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Categorisation guard — fix common AI miscategorisations under "Preliminaries"
// (mirror of CA implementation, US-aware patterns)
// ─────────────────────────────────────────────────────────────────────────────
function fixCategoryIfWrong(item: { name?: string; description?: string; category?: string }): string | undefined {
  const cat = (item.category || "").toLowerCase();
  if (!cat.includes("prelim")) return item.category;

  const blob = `${item.name || ""} ${item.description || ""}`.toLowerCase();
  if (/\b(floor framing|attic floor|floor joists?|joists?|trimmers|hangers|i[- ]?joists?)\b/.test(blob)) return "carpentry";
  if (/\b(stud framing|stud wall|wall framing|partition framing)\b/.test(blob)) return "carpentry";
  if (/\b(steel beam|w[- ]?section|ridge beam|bearing plate|lintel|header)\b/.test(blob)) return "steelwork";
  if (/\b(plant & equipment|lifting equipment|crane|telehandler|small plant)\b/.test(blob)) return "plant_equipment";
  if (/\b(scaffold|edge protection|guardrail)\b/.test(blob)) return "scaffolding";
  if (/\b(dumpster|waste removal|disposal bin)\b/.test(blob)) return "waste";
  if (/\b(demolition|strip[- ]?out|soft strip)\b/.test(blob)) return "demolition";
  return item.category;
}

// ─────────────────────────────────────────────────────────────────────────────
// Imperial unit-label normalisation
// ─────────────────────────────────────────────────────────────────────────────
//
// HISTORY: The original implementation of this function did a numeric
// conversion (qty × 10.7639 for m² → sq ft, etc.) on the assumption the AI
// would emit true metric values when it labelled an item "sqm" / "linear_m".
// In live testing the AI does the OPPOSITE: it computes quantities and rates
// in imperial scale (because the prompt emphatically demands imperial output)
// but copies the unit-label "sqm" / "linear_m" verbatim from the canonical
// template. The numeric conversion then multiplied an already-imperial 416
// into 4,477 sq ft — a 10× quote inflation bug caught in live e2e testing.
//
// Current behaviour: relabel-only. We rewrite the unit STRING to its imperial
// equivalent (sqm → sq ft, linear_m → lf, m³ → cy) and leave quantity and
// unitCost untouched. This trusts the AI's prompt-driven imperial computation,
// which is the only consistent behaviour we observe in practice.
//
// Recognised label aliases (case-insensitive, periods stripped):
//   m², m2, sqm, sq m, square meter(s)/metre(s)  → "sq ft"
//   m, lm, lin m, linear m, meter(s)/metre(s)    → "lf"
//   m³, m3, cum, cu m, cubic meter(s)/metre(s)   → "cy"
//
// Anything else is returned verbatim — no-op for already-imperial units.
//
// IMPORTANT: this function does NOT check `market`. It is a pure transform
// invoked from `localiseUsLineItems` (which IS market-gated). Keep it pure.
export interface ConvertibleItem {
  unit?: string | null;
  quantity?: number | null;
  unitCost?: number | null;
}

export function convertToImperial<T extends ConvertibleItem>(item: T): T {
  const u = (item.unit || "").trim().toLowerCase().replace(/\./g, "");
  if (!u) return item;

  let outUnit = "";

  if (u === "m²" || u === "m2" || u === "sqm" || u === "sq m" || u === "square metres" || u === "square meters" || u === "square metre" || u === "square meter") {
    outUnit = "sq ft";
  } else if (u === "m³" || u === "m3" || u === "cum" || u === "cu m" || u === "cubic metres" || u === "cubic meters" || u === "cubic metre" || u === "cubic meter") {
    outUnit = "cy";
  } else if (u === "m" || u === "lm" || u === "lin m" || u === "linear m" || u === "linear_m" || u === "linear metres" || u === "linear meters" || u === "metres" || u === "meters" || u === "metre" || u === "meter") {
    outUnit = "lf";
  } else if (u === "sq_ft" || u === "sqft" || u === "sf" || u === "ft²" || u === "ft2" || u === "square feet" || u === "square foot") {
    // Already-imperial area variants — clean to canonical "sq ft" display.
    outUnit = "sq ft";
  } else if (u === "lin_ft" || u === "lin ft" || u === "linear ft" || u === "linear feet" || u === "l/f") {
    // Already-imperial linear variants — clean to canonical "lf" display.
    outUnit = "lf";
  } else {
    return item;
  }

  const next = { ...item } as T;
  next.unit = outUnit as any;
  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// Metric-anomaly detector (non-blocking safety guard for relabel-only conversion)
// ─────────────────────────────────────────────────────────────────────────────
//
// The relabel-only `convertToImperial` is correct as long as the AI emits
// IMPERIAL-SCALE quantities even when it labels them metric (the prompt
// strongly demands imperial-scale numbers and this is what we observe in
// practice — the bug that triggered the relabel-only refactor was the AI
// emitting quantity=416 unit="sqm", which the old numeric converter then
// inflated to 4,477 sq ft).
//
// However, if the AI ever EMITS A TRUE METRIC quantity (e.g. quantity=38.7
// unit="m²" for the same 12'×14' wall), the relabel-only conversion would
// render "38.7 sq ft" — wrongly small. This detector spots that case by
// inspecting the unit-cost magnitude: imperial-band US rates (e.g. drywall
// $1.50–3.50/sq ft) are roughly 10× lower per-unit than the equivalent
// per-metric rate ($20–40/m²). When the AI emits a metric label with a cost
// in the per-metric band AND a quantity in the metric-scale range, that's a
// strong signal the AI computed the entire item in true metric — flag it.
//
// Returns a list of human-readable warnings, one per suspect item. The
// pipeline pushes these into validationFlags so the user sees them as
// review-required items in the UI rather than silently shipping a bad
// quantity. Pure function, market-gated.
export interface MetricAnomalyFlag {
  itemIndex: number;
  itemName: string;
  detail: string;
  suggestion: string;
  observedUnit: string;
  observedQuantity: number;
  observedUnitCost: number;
}

function looksLikeMetricArea(u: string): boolean {
  return u === "m²" || u === "m2" || u === "sqm" || u === "sq m" ||
    u === "square metres" || u === "square meters" ||
    u === "square metre" || u === "square meter";
}

function looksLikeMetricLength(u: string): boolean {
  return u === "m" || u === "lm" || u === "linear_m" || u === "linear m" || u === "lin m" ||
    u === "linear metres" || u === "linear meters" ||
    u === "metres" || u === "meters" || u === "metre" || u === "meter";
}

function looksLikeMetricVolume(u: string): boolean {
  return u === "m³" || u === "m3" || u === "cum" || u === "cu m" ||
    u === "cubic metres" || u === "cubic meters" ||
    u === "cubic metre" || u === "cubic meter";
}

export function detectUsMetricAnomalies<T extends { name?: string; unit?: string; quantity?: number; unitCost?: number }>(
  items: T[],
  market?: string | null
): MetricAnomalyFlag[] {
  if (market !== "us" || !Array.isArray(items)) return [];

  const flags: MetricAnomalyFlag[] = [];
  items.forEach((item, idx) => {
    const u = (item.unit || "").trim().toLowerCase().replace(/\./g, "");
    const cost = typeof item.unitCost === "number" ? item.unitCost : 0;
    const qty = typeof item.quantity === "number" ? item.quantity : 0;
    if (!u || cost <= 0 || qty <= 0) return;

    // Area items: imperial-band caps top out around $30/sq ft (brickwork,
    // tiling). Per-m² rates for everyday trades are $16–$60/m² (drywall,
    // paint, insulation, framing). If cost > $20 and quantity is in the
    // plausible m² range for residential (< 100 m² ≈ < 1,076 sq ft), it's
    // very likely a true-metric leak. We accept the risk of a few
    // false-positives in the $20–$30/sq ft range (premium tile, brickwork)
    // because the flag is non-blocking — the user just sees a banner asking
    // them to verify the line, which is exactly what we want when ambiguous.
    if (looksLikeMetricArea(u) && cost > 20 && qty < 100) {
      flags.push({
        itemIndex: idx,
        itemName: item.name || `Item ${idx + 1}`,
        observedUnit: item.unit || "",
        observedQuantity: qty,
        observedUnitCost: cost,
        detail: `Suspect true-metric quantity: "${item.name || ""}" came back as ${qty} ${item.unit} @ $${cost}. US imperial bands are $1.50–$30/sq ft, so a unit-cost of $${cost} suggests a per-m² rate. After unit relabeling this will display as "${qty} sq ft" which is likely 10.76× too small.`,
        suggestion: `Verify the quantity is in sq ft. If the AI computed in m², multiply by 10.76 (≈ ${Math.round(qty * 10.7639)} sq ft) and divide the unit-cost by 10.76 (≈ $${(cost / 10.7639).toFixed(2)}/sq ft).`,
      });
    }

    // Length items: imperial-band caps top out around $50/lf (premium trim).
    // Per-m rates typically $10–$300+. Threshold cost > $50 and qty < 30.
    if (looksLikeMetricLength(u) && cost > 50 && qty < 30) {
      flags.push({
        itemIndex: idx,
        itemName: item.name || `Item ${idx + 1}`,
        observedUnit: item.unit || "",
        observedQuantity: qty,
        observedUnitCost: cost,
        detail: `Suspect true-metric quantity: "${item.name || ""}" came back as ${qty} ${item.unit} @ $${cost}. US imperial bands are $3–$50/lf, so a unit-cost of $${cost} suggests a per-m rate. After unit relabeling this will display as "${qty} lf" which is likely 3.28× too small.`,
        suggestion: `Verify the quantity is in lf. If the AI computed in metres, multiply by 3.28 (≈ ${Math.round(qty * 3.2808)} lf) and divide the unit-cost by 3.28 (≈ $${(cost / 3.2808).toFixed(2)}/lf).`,
      });
    }

    // Volume items: per-m³ and per-cy rates overlap heavily ($150–$700 both
    // sides), so magnitude alone is unreliable. Use quantity-only signal:
    // residential m³ jobs typically < 5 m³, while cy quantities are usually
    // > 5 cy. If qty < 5 with metric label, flag it.
    if (looksLikeMetricVolume(u) && qty < 5) {
      flags.push({
        itemIndex: idx,
        itemName: item.name || `Item ${idx + 1}`,
        observedUnit: item.unit || "",
        observedQuantity: qty,
        observedUnitCost: cost,
        detail: `Suspect true-metric quantity: "${item.name || ""}" came back as ${qty} ${item.unit} @ $${cost}. After unit relabeling this will display as "${qty} cy" which is likely 1.31× too small.`,
        suggestion: `Verify the quantity is in cubic yards. If the AI computed in m³, multiply by 1.31 (≈ ${(qty * 1.30795).toFixed(1)} cy) and divide the unit-cost by 1.31 (≈ $${(cost / 1.30795).toFixed(2)}/cy).`,
      });
    }
  });

  return flags;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────
export function localiseText(text: string, market?: string | null): string {
  if (market !== "us" || !text) return text;
  let out = text;
  for (const [re, repl] of US_TERMINOLOGY) {
    out = typeof repl === "function" ? out.replace(re, repl as any) : out.replace(re, repl);
  }
  return out;
}

export function applyUsRateCap<T extends { unitCost?: number; unit?: string; category?: string; name?: string }>(item: T, market?: string | null): T {
  if (market !== "us" || typeof item.unitCost !== "number") return item;
  const key = resolveCapKey(item.category, item.name);
  if (!key) return item;
  const cap = US_RATE_CAPS[key];
  const u = (item.unit || "").toLowerCase();
  if (!cap.units.includes(u)) return item;
  if (item.unitCost > cap.maxUnitCost) {
    return { ...item, unitCost: cap.maxUnitCost };
  }
  if (typeof cap.minUnitCost === "number" && item.unitCost > 0 && item.unitCost < cap.minUnitCost) {
    return { ...item, unitCost: cap.minUnitCost };
  }
  return item;
}

export function fixUsCategory<T extends { name?: string; description?: string; category?: string }>(item: T, market?: string | null): T {
  if (market !== "us") return item;
  const newCat = fixCategoryIfWrong(item);
  if (newCat && newCat !== item.category) return { ...item, category: newCat };
  return item;
}

export function localiseUsLineItems<T extends { name?: string; description?: string; inclusions?: string[]; exclusions?: string[]; unitCost?: number; unit?: string; quantity?: number; category?: string }>(items: T[], market?: string | null): T[] {
  if (market !== "us" || !Array.isArray(items)) return items;
  let out = items.map(item => {
    let next: T = { ...item };
    // 1. Terminology pass on every free-text field. The model often stashes
    //    UK vocabulary inside the inclusions/exclusions bullet lists ("Plasterboard
    //    supply", "Ironmongery fitting", "Frame (included in door lining)") so
    //    localising name + description alone is not enough — those bullets surface
    //    in the customer-facing PDF.
    if (typeof next.name === "string") next.name = localiseText(next.name, market) as any;
    if (typeof next.description === "string") next.description = localiseText(next.description, market) as any;
    if (Array.isArray(next.inclusions)) {
      next.inclusions = next.inclusions.map(s => typeof s === "string" ? localiseText(s, market) : s) as any;
    }
    if (Array.isArray(next.exclusions)) {
      next.exclusions = next.exclusions.map(s => typeof s === "string" ? localiseText(s, market) : s) as any;
    }
    // 2. Categorisation guard.
    next = fixUsCategory(next, market);
    // 2b. Tile backer board mis-emission fix.
    //    The model sometimes emits a generic "Carpentry" line for cement/backer board
    //    install with unit:"item" and quantity equal to the actual sq-ft area
    //    (e.g. quantity:85, unit:"item", $6 each = $510). Treating that as 85 "items"
    //    is wrong; it's 85 sq ft of backer board at $6/sq ft. Detect and correct
    //    so the unit/qty match the description.
    next = fixUsBackerBoardUnit(next);
    // 3. Convert metric → imperial (preserves subtotal). No-op if already imperial.
    next = convertToImperial(next);
    // 4. Rate cap (in imperial units, post-conversion).
    next = applyUsRateCap(next, market);
    return next;
  });
  // 5. Bathroom area sanity clamp — runs LAST, after units are imperial.
  //    Catches the dimensionless-bathroom inflation bug where the AI invents
  //    different room sizes for different wall-surface items (e.g. floor tile
  //    35 sq ft → 5x7 bathroom, but drywall 915 sq ft → 30x30 room). See
  //    `clampUsBathroomAreas` for the full per-item ceiling table and the
  //    item-scoped bathroom-scope detector.
  out = clampUsBathroomAreas(out);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// US bathroom area sanity clamp
// ─────────────────────────────────────────────────────────────────────────────
//
// Belt-and-braces guard against the AI inventing wildly inconsistent room
// dimensions across wall-surface items in a single bathroom quote. The prompt
// (US BATHROOM AREA REFERENCE block in buildUsMarketContext) already gives
// the AI explicit ceilings and a "derive everything from one footprint" rule,
// but if the AI ignores the prompt this clamp catches and corrects it.
//
// Item-scoped: for each per-sqft wall-surface line, we first check whether
// THAT line itself describes a bathroom / wet-area scope via
// `itemHasBathroomScope` (its own name/description/category). Only then do
// we apply the published hard-ceiling. There is no quote-wide signal —
// a mixed-scope quote (loft conversion + small en-suite, kitchen extension
// that mentions a powder room incidentally) must NOT clip the unrelated
// large surface items. Subtotal is recomputed via qty × unitCost downstream
// so we only need to mutate `quantity`.
//
// Ceilings here MUST stay in sync with the "NEVER above X sq ft" lines in
// buildUsMarketContext's "US BATHROOM AREA REFERENCE" block.
// Order matters: more-specific rules (backer board, drywall ceilings, drywall
// finishing) come BEFORE more-general rules (wall tiling, drywall to walls,
// decorating) so the specific rules claim their items first.
const US_BATHROOM_AREA_CEILINGS: Array<{ match: (n: string, c: string) => boolean; ceiling: number; label: string }> = [
  // Backer board — MUST come before "Wall Tiling" because the AI sometimes
  // names it "Tile Backer Board" which would otherwise be caught by the
  // wall-tile rule (the word "Tile" is in the name).
  { match: (n) => /backer\s*board|cement\s*board|hardie\s*backer|durock|wonderboard/i.test(n), ceiling: 240, label: "Tile Backer Board" },
  // Waterproofing — distinct keyword, no overlap with anything below.
  { match: (n) => /water[- ]?proof|tanking|membrane|kerdi|hydroban|red\s*gard/i.test(n), ceiling: 160, label: "Waterproofing" },
  // Drywall finishing (tape & mud) — MUST come before generic drywall-to-walls.
  { match: (n) => /(drywall|plasterboard|tape|mud|skim|level\s*[345]).*(finish|tape|mud|skim)/i.test(n) || /^(drywall finishing|plastering|skim coat|tape\s*&?\s*finish|level\s*[345])/i.test(n), ceiling: 450, label: "Drywall Finishing" },
  // Drywall to ceilings — MUST come before generic drywall-to-walls. Matches
  // both word orders ("Drywall to Ceiling" and "Ceiling Drywall" — the latter
  // is created by the terminology rule "Ceiling Plasterboard" → "Ceiling
  // Drywall") plus generic ceiling-board variants.
  { match: (n) => /drywall.*ceiling|plasterboard.*ceiling|board.*ceiling|ceiling.*drywall|ceiling.*plasterboard|ceiling\s*board\b/i.test(n), ceiling: 110, label: "Drywall to Ceilings" },
  // Drywall to walls — generic drywall catch-all. Excludes ceiling variants
  // (caught above) and any finishing variants (caught above).
  { match: (n) => /\b(drywall|plasterboard|dot\s*&?\s*dab|direct[- ]?applied)\b/i.test(n) && !/ceiling/i.test(n) && !/finish|tape|mud|skim|level\s*[345]/i.test(n), ceiling: 350, label: "Drywall to Walls" },
  // Floor tiling — name must contain "floor" + "til" (e.g. "Floor Tiling")
  // OR category exactly equals "tiling"/"flooring" AND name contains "floor".
  { match: (n, c) => /\bfloor\s*til/i.test(n) || (/^(tiling|flooring)$/i.test((c || "").trim()) && /floor/i.test(n)), ceiling: 110, label: "Floor Tiling" },
  // Wall tiling — name must explicitly say "wall" + "til" (e.g. "Wall Tiling")
  // OR category exactly equals "tiling" AND name contains "wall".
  // CRITICAL: category check uses ^...$ to avoid matching "drywall" via /wall/.
  { match: (n, c) => /\bwall\s*til/i.test(n) || (/^tiling$/i.test((c || "").trim()) && /\bwall\b/i.test(n)), ceiling: 220, label: "Wall Tiling" },
  // Decorating — paintable surface area. Wide net but only triggered when
  // unit is sq ft and bathroom context is present (both pre-checked).
  { match: (n, c) => /decorat|paint/i.test(n) || /^decorating$/i.test((c || "").trim()), ceiling: 280, label: "Decorating" },
];

export interface BathroomAreaClampFlag {
  itemIndex: number;
  itemName: string;
  originalQuantity: number;
  clampedQuantity: number;
  ceilingApplied: number;
  rule: string;
}

// Item-scoped bathroom signal — true when the item's OWN name/description/
// category indicates a bathroom or wet-area scope. We deliberately do NOT
// look at the rest of the quote, because a mixed-scope quote (e.g. a loft
// conversion that also adds a small en-suite) must NOT clamp the generic
// loft drywall just because the en-suite is mentioned elsewhere.
//
// In a real bathroom quote, every wet-area sq-ft line item describes itself
// as such — empirical examples from the AI output:
//   "Tape, finish, and skim-coat ... drywall finishing to bathroom walls"
//   "Liquid-applied waterproofing membrane to shower enclosure walls"
//   "Supply and install ... floor tile to bathroom floor"
//   "cement tile backer board to shower walls and other wet-area substrates"
//   "full-height wall tile to shower wet area including niche returns"
//   "Prime and paint bathroom ceiling, non-tiled wall areas"
//   "1/2 in drywall to bathroom walls and ceiling areas"
// So an item-scoped signal is sufficient to catch the Q-2026-0379 class of
// inflation without the false-positive risk of quote-wide activation.
function itemHasBathroomScope<T extends { name?: string; description?: string; category?: string }>(it: T): boolean {
  const n = (it.name || "").toLowerCase();
  const d = (it.description || "").toLowerCase();
  const c = (it.category || "").toLowerCase().trim();
  if (c === "bathrooms" || c === "bathroom_fitting" || c === "bathroom") return true;
  // Bathroom-room words. Note: \bbath\b is accepted in name/description because
  // it's almost always a room reference in a construction context (we have not
  // observed it being used to mean a paint "bath" or a sealant "bath" in any
  // AI-generated output to date). If a future regression surfaces, narrow this
  // to `\bbath\s*room\b` only.
  const bathroomWords = /\bbathrooms?\b|\bbath\b|\ben[- ]?suite\b|\bensuite\b|\bshower\s*room\b|\bwet\s*(room|area|zone)\b|\bpowder\s*room\b|\bhalf[- ]?bath\b|\bfull[- ]?bath\b|\bprimary\s*bath\b|\bmaster\s*bath\b/i;
  if (bathroomWords.test(n) || bathroomWords.test(d)) return true;
  // Shower / wet-area fixtures referenced inside the item itself
  // (e.g. "Liquid-applied waterproofing to shower enclosure"). NOTE we
  // deliberately do NOT include "splashback" here — kitchen splashbacks
  // are common and would create a false positive.
  const wetAreaInItem = /\bshower\s*(enclosure|stall|screen|wall|tray|pan|niche)\b|\bwet[- ]?area\b|\btub\s*surround\b/i;
  if (wetAreaInItem.test(n) || wetAreaInItem.test(d)) return true;
  return false;
}

export function clampUsBathroomAreas<T extends { name?: string; description?: string; category?: string; unit?: string; quantity?: number }>(items: T[], flags?: BathroomAreaClampFlag[]): T[] {
  if (!Array.isArray(items) || items.length === 0) return items;
  return items.map((item, idx) => {
    const u = (item.unit || "").trim().toLowerCase();
    if (u !== "sq ft") return item;
    const qty = typeof item.quantity === "number" ? item.quantity : 0;
    if (qty <= 0) return item;
    // Item-scoped activation: clamp only when THIS item is itself in
    // bathroom / wet-area scope. Prevents mixed-scope quotes (loft +
    // en-suite, kitchen extension + powder room mention) from clipping
    // unrelated large drywall / decorating lines.
    if (!itemHasBathroomScope(item)) return item;
    const name = (item.name || "");
    const cat = (item.category || "");
    for (const rule of US_BATHROOM_AREA_CEILINGS) {
      if (!rule.match(name, cat)) continue;
      if (qty <= rule.ceiling) return item;
      if (Array.isArray(flags)) {
        flags.push({
          itemIndex: idx,
          itemName: name,
          originalQuantity: qty,
          clampedQuantity: rule.ceiling,
          ceilingApplied: rule.ceiling,
          rule: rule.label,
        });
      }
      return { ...item, quantity: rule.ceiling };
    }
    return item;
  });
}

// Tile backer board mis-emission: AI sometimes returns name="Carpentry" with
// unit="item" + quantity equal to the area in sq ft (e.g. 85 item @ $6 = $510,
// when the actual scope is 85 sq ft of HardieBacker at $6/sq ft). Detect via
// description signals and re-shape the line so the unit matches the scope.
function fixUsBackerBoardUnit<T extends { name?: string; description?: string; quantity?: number; unit?: string; category?: string }>(item: T): T {
  const desc = (item.description || "").toLowerCase();
  const isBacker = /\b(?:tile\s+)?backer\s*board\b|\bcement\s*board\b|\bcementitious\s+(?:tile\s+)?(?:backer|substrate)\b|\bdurarock\b|\bhardiebacker\b|\bwonderboard\b/i.test(desc);
  if (!isBacker) return item;
  const u = (item.unit || "").toLowerCase().trim();
  const isUnitlessBulk = u === "item" || u === "items" || u === "ea" || u === "each" || u === "" || u === "no" || u === "no.";
  const qty = Number(item.quantity || 0);
  // Only act when the unit is generic AND the quantity reads as an area (>5).
  // Genuine "1 item" or "2 each" lump-sum lines are left alone.
  if (!isUnitlessBulk || qty <= 5) return item;
  const next = { ...item };
  next.unit = "sq ft" as any;
  if (typeof next.name === "string" && /^carpentry$|^miscellaneous|^general$/i.test(next.name.trim())) {
    next.name = "Tile Backer Board" as any;
  }
  if (!next.category || /carpentry|other|misc/i.test(next.category)) {
    next.category = "drywall" as any;
  }
  return next;
}
